// The draft editor's model (E31-S6-T2; ADR-035; mockups/content-edit.html): who may edit, the body PUT
// /v1/content/versions/:id takes, and the checks the page makes before it sends one. The server checks
// again (apps/api/src/modules/learning/versions/first-draft.ts checkFirstDraft) and its 400 names the
// fields, which fieldOf maps back onto the screen.
//
// The body replaces the draft whole: the script, the question set, the theme, the content version and
// every still the script names, each with its bytes (base64), its type and the sha256 of those exact
// bytes. A still that is not replaced is fetched from the version's still route and sent back as it is.
import type { Principal, Scene, Script } from "@waypoint/contracts";
import {
  pagesWithin,
  parsePages,
  parseQuestions,
  validateQuestions,
} from "@waypoint/content/questions";
import type { VersionDetail } from "./model.js";

/** A still's largest size before base64 (first-draft.ts MAX_STILL_BYTES). */
export const STILL_MAX_BYTES = 8 * 1024 * 1024;
/** The whole PUT body's limit (first-draft.ts FIRST_DRAFT_BODY_LIMIT). */
export const BODY_MAX_BYTES = 32 * 1024 * 1024;
export const MIB = 1024 * 1024;

/** Draws the Edit action: a draft, for a content owner (content.author). The API decides again. */
export function canEdit(v: Pick<VersionDetail, "id" | "state">, principal: Principal): boolean {
  return v.id !== undefined && v.state === "draft" && principal.roles.includes("content-owner");
}

export const editPath = (id: string) => `/content/versions/${encodeURIComponent(id)}/edit`;
export const reviewPath = (id: string) => `/content/versions/${encodeURIComponent(id)}/review`;

// ── stills ───────────────────────────────────────────────────────────────────────────────────────

export type StillType = "image/png" | "image/jpeg";

export interface ImageSize {
  readonly contentType: StillType;
  readonly width: number;
  readonly height: number;
}

const PNG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const isFrame = (m: number) => m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc;

/**
 * A PNG's or JPEG's type and pixel size from its first bytes, or undefined for anything else: the same
 * reading as apps/api's image-size.ts, which the render request uses. Nothing is decoded.
 */
export function imageSize(bytes: Uint8Array): ImageSize | undefined {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.byteLength >= 24 && PNG.every((b, i) => bytes[i] === b)) {
    const width = view.getUint32(16);
    const height = view.getUint32(20);
    return width > 0 && height > 0 ? { contentType: "image/png", width, height } : undefined;
  }
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return undefined;
  let at = 2;
  while (at + 4 <= bytes.byteLength) {
    if (bytes[at] !== 0xff) return undefined;
    const marker = bytes[at + 1] ?? 0;
    if (marker === 0xff) {
      at += 1;
      continue;
    }
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) {
      at += 2;
      continue;
    }
    if (isFrame(marker)) {
      if (at + 9 > bytes.byteLength) return undefined;
      const height = view.getUint16(at + 5);
      const width = view.getUint16(at + 7);
      return width > 0 && height > 0 ? { contentType: "image/jpeg", width, height } : undefined;
    }
    at += 2 + view.getUint16(at + 2);
  }
  return undefined;
}

/** The sha256 of exactly these bytes, as lowercase hex (the body's `sha256`). */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes as Uint8Array<ArrayBuffer>);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Base64 of the bytes, in chunks so a large still does not overflow the argument list. */
export function base64Of(bytes: Uint8Array): string {
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK)
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  return btoa(binary);
}

/** How long base64 makes `n` bytes: about a third more. */
export const base64Length = (n: number) => 4 * Math.ceil(n / 3);

/** A still the editor holds: read and hashed, so it can go into the body as it is. */
export interface HeldStill extends ImageSize {
  readonly id: string;
  readonly sha256: string;
  readonly bytes: Uint8Array;
  /** The file it was read from, for a replaced still. */
  readonly fileName?: string;
}

/** Reads a still's bytes into a HeldStill, or says why it can't be used. */
export async function holdStill(
  id: string,
  bytes: Uint8Array,
  fileName?: string,
): Promise<HeldStill | { refused: "type" | "size" }> {
  if (bytes.byteLength > STILL_MAX_BYTES) return { refused: "size" };
  const size = imageSize(bytes);
  if (size === undefined) return { refused: "type" };
  const sha256 = await sha256Hex(bytes);
  return { id, sha256, bytes, ...size, ...(fileName === undefined ? {} : { fileName }) };
}

/** A replaced still's id: from its hash, so two scenes given the same file share one still. */
export const replacedStillId = (sha256: string) => `still-${sha256.slice(0, 16)}`;

// ── scenes ───────────────────────────────────────────────────────────────────────────────────────

export interface Box {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** What the editor changes in a scene; everything else in it is carried through as it was. */
export interface SceneDraft {
  readonly narration: string;
  readonly pages: string;
  /** Still and video scenes only. */
  readonly stillId?: string;
  /** Still scenes only; null is "No highlight". Numbers may be NaN while a field is empty. */
  readonly box?: Box | null;
}

export const hasStill = (s: Scene): s is Extract<Scene, { stillId: string }> =>
  s.kind === "still" || s.kind === "video";

export function draftOf(scene: Scene): SceneDraft {
  return {
    narration: scene.narration,
    pages: scene.citation.pages,
    ...(hasStill(scene) ? { stillId: scene.stillId } : {}),
    ...(scene.kind === "still" ? { box: scene.box ?? null } : {}),
  };
}

/** A box in the middle third of the still, for "Add a highlight box". */
export function centredBox(still: Pick<ImageSize, "width" | "height">): Box {
  const w = Math.max(1, Math.round(still.width / 3));
  const h = Math.max(1, Math.round(still.height / 3));
  return { x: Math.round((still.width - w) / 2), y: Math.round((still.height - h) / 2), w, h };
}

export type SceneProblem =
  | { readonly field: "narration"; readonly kind: "empty" | "long" }
  | { readonly field: "pages"; readonly kind: "empty" | "format" | "outside" }
  | { readonly field: "box"; readonly kind: "whole" | "right" | "bottom" };

/** A typed hyphen between two page numbers becomes the en dash the page grammar uses. */
export const normalisePages = (pages: string) => pages.trim().replace(/(\d)\s*-\s*(\d)/g, "$1–$2");

export function sceneProblems(
  draft: SceneDraft,
  still: Pick<ImageSize, "width" | "height"> | undefined,
  guidePages: string | undefined,
): SceneProblem[] {
  const out: SceneProblem[] = [];
  const text = draft.narration.trim();
  if (text === "") out.push({ field: "narration", kind: "empty" });
  else if (text.length > 4000) out.push({ field: "narration", kind: "long" });
  const pages = normalisePages(draft.pages);
  const cited = parsePages(pages);
  const guide = guidePages === undefined ? undefined : parsePages(guidePages);
  if (pages === "") out.push({ field: "pages", kind: "empty" });
  else if (cited === undefined) out.push({ field: "pages", kind: "format" });
  else if (guide !== undefined && !pagesWithin(cited, guide))
    out.push({ field: "pages", kind: "outside" });
  const box = draft.box;
  if (box !== undefined && box !== null) {
    const { x, y, w, h } = box;
    if (![x, y, w, h].every(Number.isInteger) || x < 0 || y < 0 || w < 1 || h < 1)
      out.push({ field: "box", kind: "whole" });
    else if (still !== undefined && x + w > still.width) out.push({ field: "box", kind: "right" });
    else if (still !== undefined && y + h > still.height)
      out.push({ field: "box", kind: "bottom" });
  }
  return out;
}

/** The scene with the editor's changes applied; kind, heading, statement and zoom as they were. */
export function sceneWith(scene: Scene, draft: SceneDraft): Scene {
  const citation = { ...scene.citation, pages: normalisePages(draft.pages) };
  const narration = draft.narration.trim();
  switch (scene.kind) {
    case "title":
    case "avatar":
      return { ...scene, narration, citation };
    case "video":
      return { ...scene, narration, citation, stillId: draft.stillId ?? scene.stillId };
    case "still": {
      const { box: _box, ...rest } = scene;
      const box = draft.box === undefined ? scene.box : (draft.box ?? undefined);
      return {
        ...rest,
        narration,
        citation,
        stillId: draft.stillId ?? scene.stillId,
        ...(box === undefined ? {} : { box }),
      };
    }
  }
}

// ── the question set ─────────────────────────────────────────────────────────────────────────────

export interface QuestionProblem {
  readonly line: number;
  readonly message: string;
}

/**
 * The question set through the pack's own parser and validator (@waypoint/content/questions), as the
 * server runs them on save: no beats on a draft. Without the title's guide pages every page counts as
 * inside them here, and the server checks the range.
 */
export function questionProblems(
  markdown: string,
  ctx: { titleId: string; doc: string; docVersion: string; guidePages: string | undefined },
): QuestionProblem[] {
  const parsed = parseQuestions(markdown, {
    titleId: ctx.titleId,
    doc: ctx.doc,
    docVersion: ctx.docVersion,
  });
  const errors =
    parsed.check === undefined
      ? parsed.errors
      : validateQuestions(parsed.check, {
          guidePages: ctx.guidePages ?? "pp.1–99999",
          beatIds: [],
          ...(parsed.lines === undefined ? {} : { lines: parsed.lines }),
        });
  return errors.map((e) => ({ line: e.line, message: e.message })).sort((a, b) => a.line - b.line);
}

// ── the body ─────────────────────────────────────────────────────────────────────────────────────

export interface EditBody {
  script: Script;
  questions: string;
  theme: string;
  contentVersion?: string;
  stills: { id: string; sha256: string; contentType: StillType; data: string }[];
}

/** The stills the scenes name, once each, in scene order. */
export function namedStills(
  drafts: readonly SceneDraft[],
  held: ReadonlyMap<string, HeldStill>,
): HeldStill[] {
  const seen = new Set<string>();
  const out: HeldStill[] = [];
  for (const d of drafts) {
    if (d.stillId === undefined || seen.has(d.stillId)) continue;
    seen.add(d.stillId);
    const still = held.get(d.stillId);
    if (still !== undefined) out.push(still);
  }
  return out;
}

/** About how big the PUT body is: every named still as base64, plus the text. */
export function bodySize(
  drafts: readonly SceneDraft[],
  held: ReadonlyMap<string, HeldStill>,
  text: number,
): number {
  return (
    namedStills(drafts, held).reduce((n, s) => n + base64Length(s.bytes.byteLength) + 200, 0) + text
  );
}

export function buildBody(
  version: Pick<VersionDetail, "script" | "theme">,
  drafts: readonly SceneDraft[],
  held: ReadonlyMap<string, HeldStill>,
  questions: string,
  contentVersion: string,
): EditBody {
  return {
    script: {
      ...version.script,
      scenes: version.script.scenes.map((scene, i) => {
        const draft = drafts[i];
        return draft === undefined ? scene : sceneWith(scene, draft);
      }),
    },
    questions,
    theme: version.theme,
    contentVersion: contentVersion.trim(),
    stills: namedStills(drafts, held).map((s) => ({
      id: s.id,
      sha256: s.sha256,
      contentType: s.contentType,
      data: base64Of(s.bytes),
    })),
  };
}

/** Where a 400's field lands on screen (first-draft.ts names `body.<path>`). */
export type FieldPlace =
  | {
      readonly at: "scene";
      readonly index: number;
      readonly field: "pages" | "narration" | "still" | "box";
    }
  | { readonly at: "questions" }
  | { readonly at: "contentVersion" }
  | { readonly at: "other" };

export function fieldOf(path: string, drafts: readonly SceneDraft[]): FieldPlace {
  const p = path.replace(/^body\./, "");
  const scene = /^script\.scenes\.(\d+)(?:\.(.+))?$/.exec(p);
  if (scene) {
    const index = Number(scene[1]);
    const rest = scene[2] ?? "";
    const field = rest.startsWith("citation")
      ? "pages"
      : rest.startsWith("narration")
        ? "narration"
        : rest.startsWith("box")
          ? "box"
          : "still";
    return { at: "scene", index, field };
  }
  const still = /^stills\.(\d+)/.exec(p);
  if (still) {
    let n = 0;
    const seen = new Set<string>();
    for (const [index, d] of drafts.entries()) {
      if (d.stillId === undefined || seen.has(d.stillId)) continue;
      seen.add(d.stillId);
      if (n === Number(still[1])) return { at: "scene", index, field: "still" };
      n += 1;
    }
    return { at: "other" };
  }
  if (p.startsWith("questions")) return { at: "questions" };
  if (p.startsWith("contentVersion")) return { at: "contentVersion" };
  return { at: "other" };
}
